import { NetworkStatus } from '@apollo/client';
import React from 'react';

import { useClientSetup } from './clientSetup';
import type { StepProps } from './types';

/**
 * The client setup a step's form edits. `data` stays undefined until the
 * server answers, even with cached data: a form keeps its first values, so
 * starting from a stale cache could overwrite newer settings on save. Once it
 * has answered, a failed reload keeps the last data, so the form and its
 * unsaved edits stay on screen, and `reloadError` says why the form must not
 * save yet.
 *
 * Until there's data, it registers a save that refuses, since with no
 * handler the flow would record the step as saved without saving it. Once
 * there is, the form registers its own save.
 */
export const useStepSetup = (
  clientId: string | undefined,
  registerSave: StepProps['registerSave'],
  /** Plural, e.g. "eligibility settings". */
  settingsName: string,
) => {
  const setup = useClientSetup(clientId);
  const firstLoad = setup.networkStatus === NetworkStatus.loading;
  const [loadedFromServer, setLoadedFromServer] = React.useState(false);
  if (
    !loadedFromServer &&
    setup.networkStatus === NetworkStatus.ready &&
    !setup.error &&
    setup.data
  ) {
    setLoadedFromServer(true);
  }
  const data = loadedFromServer
    ? (setup.data ?? setup.previousData)
    : undefined;
  const ready = Boolean(data?.client);

  React.useEffect(() => {
    if (ready || !registerSave) {
      return;
    }
    registerSave(async () => {
      throw new Error(
        firstLoad
          ? `Wait for the ${settingsName} to load.`
          : `The ${settingsName} didn't load, so they can't be saved.`,
      );
    });
    return () => registerSave(undefined);
  }, [ready, firstLoad, registerSave, settingsName]);

  const retry = React.useCallback(() => {
    setup.refetch().catch(() => undefined);
  }, [setup.refetch]);

  return {
    setup,
    firstLoad,
    data,
    reloadError: data ? setup.error?.message : undefined,
    retry,
  };
};
